import { useMemo } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import { tagular } from "@cohesion/tagular";

import { getThemeClasses } from "@utils/bestInTravel/2025/getThemeClasses";
import { useBestInTravel } from "@context/bestInTravel/2025/bestInTravelContext";
import { keyify } from "@utils/keyify";

import { Arrow } from "@icons/elsewhere";
import { Dropdown } from "@components/dropdown";
import { ChevronRight } from "@icons";

export function MapNavigation({ routeCategory = null }) {
  const { setDestinationsMenuOpen, destinations, screen } = useBestInTravel();
  const isMobileNavigation = screen === "sm";
  const categories = ["countries", "regions", "cities"];

  const findFirstDestinationByCategory = useMemo(
    () => category => {
      if (!destinations || destinations.length === 0) {
        return null;
      }
      const destination = destinations.find(
        dest => dest.meta.category === category
      );
      return destination ? `#${keyify(destination.name)}` : null;
    },
    [destinations]
  );

  function handleStartExploring() {
    setDestinationsMenuOpen(true);
    tagular("click", {
      actionOutcome: "OPEN",
      outboundUrl: "null",
      webElement: {
        location: "BIT_Map_Tabs",
        elementType: "BUTTON",
        text: "Start Exploring",
        position: "0",
      },
    });
    const hash = findFirstDestinationByCategory(routeCategory || "countries");
    if (!hash) {
      return null;
    }
    window.location.hash = "";
    window.location.hash = hash;
    return true;
  }

  return (
    <div className="flex flex-col sm:flex-row sm:border-b-2 sm:border-sand-300 sm:py-2 sm:px-8 sm:items-center ">
      <div className="w-full py-2 text-2xl font-normal text-center font-instrument text-blue sm:text-left sm:text-4xl">
        Discover the winners
      </div>
      {isMobileNavigation && (
        <div className="container relative py-4 border-t border-sand-300 custom-select">
          <ChevronRight className="absolute rotate-90 pointer-events-none top-7 right-8 text-blue" />
          <Dropdown
            id="dropdown-destinations"
            label="Select destinations"
            hideLabel
            className="font-semibold capitalize bg-opacity-0 text-blue border-blue bg-none"
            defaultValue={routeCategory || "All Destinations"}
            onChange={e => {
              const selectedCategory = e.target.value;
              // Validate selectedCategory
              if (
                selectedCategory === "All Destinations" ||
                categories.includes(selectedCategory)
              ) {
                let pos = 0;
                if (selectedCategory === "cities") {
                  pos = 1;
                }
                if (selectedCategory === "countries") {
                  pos = 2;
                }
                if (selectedCategory === "regions") {
                  pos = 3;
                }
                tagular("click", {
                  actionOutcome: "INTERNALLINK",
                  outboundUrl:
                    selectedCategory === "All Destinations"
                      ? "/best-in-travel/#map"
                      : `/best-in-travel/${selectedCategory}/#map`,
                  webElement: {
                    location: "BIT_Map_Tabs_Mobile",
                    elementType: "LINK",
                    text: selectedCategory,
                    position: pos,
                  },
                });

                window.location.href =
                  selectedCategory === "All Destinations"
                    ? `/best-in-travel/#map`
                    : `/best-in-travel/${selectedCategory}/#map`;
              }
            }}
          >
            <option value="All Destinations">All Destinations</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </Dropdown>
        </div>
      )}
      <div className="items-center justify-center hidden gap-2 px-10 py-2 border-b border-sand-300 sm:border-none md:flex">
        <a
          key="All Destinations"
          type="button"
          href="/best-in-travel/#map"
          className={cn(
            "w-content rounded-pill px-4 py-2 font-semibold text-sm h-10 border hidden lg:flex",
            routeCategory
              ? "bg-transparent border-black-200 text-blue"
              : "bg-blue border-blue text-white pointer-events-none"
          )}
          onClick={() => {
            tagular("click", {
              actionOutcome: "INTERNAL LINK",
              outboundUrl: "/best-in-travel/#map",
              webElement: {
                location: "BIT_Map_Tabs",
                elementType: "BUTTON",
                text: "All Destinations",
                position: "0",
              },
            });
          }}
        >
          All Destinations
        </a>
        {/* CATEGORIES BUTTONS */}
        {categories.map((category, i) => {
          const buttonThemeClasses = getThemeClasses(category);
          return (
            <a
              key={category}
              href={`/best-in-travel/${category}/#map`}
              className={cn(
                "capitalize w-content rounded-pill px-4 py-2 font-semibold text-sm h-10 border",
                routeCategory === category
                  ? buttonThemeClasses.border
                  : "bg-transparent border-black-200",
                buttonThemeClasses.background,
                buttonThemeClasses.text
              )}
              onClick={() => {
                tagular("click", {
                  actionOutcome: "INTERNALLINK",
                  outboundUrl: `/best-in-travel/${category}/#map`,
                  webElement: {
                    location: `BIT_Map_Tabs`,
                    elementType: "BUTTON",
                    name: category,
                    position: i,
                  },
                });
              }}
            >
              {category}
            </a>
          );
        })}
      </div>

      {/* START EXPLORING BTN */}
      <button
        type="button"
        className="items-center hidden h-10 pl-10 font-extrabold border-l font-neue border-black-200 whitespace-nowrap text-blue lg:flex"
        onClick={() => handleStartExploring()}
      >
        Start Exploring
        <Arrow className="w-4 h-4 ml-2" />
      </button>
    </div>
  );
}

MapNavigation.propTypes = {
  routeCategory: PropTypes.string,
};
